import React, { useState, useMemo } from 'react';
import { Button, Form, Dropdown, Message, Segment, Header, Icon, Tab, TextArea } from 'semantic-ui-react';
import { decodeProto } from './protobufDecoder';
import { xorBuffer, xorHexString } from './xorUtils';
import { bufferToPrettyHex, parseInput } from './hexUtils';
import ProtobufDisplay from './ProtobufDisplay';
import { useI18n } from './i18n';

function getFieldValue(parts, fieldNumber) {
  for (const part of parts) {
    if (part.index === fieldNumber) {
      return part;
    }
  }
  return null;
}

function checkFieldCondition(field, condition) {
  if (!field || !condition) return false;
  
  const { type, value, expectedValue } = condition;
  
  if (type === 'varint') {
    if (field.type === 0) { // VARINT
      const fieldValue = parseInt(field.value.toString());
      return fieldValue === expectedValue;
    }
  }
  
  return false;
}

function SmartDecoder({ originalData, onDecodeResult, onApplyBytes }) {
  const { t } = useI18n();
  const [conditionField, setConditionField] = useState(6);
  const [conditionValue, setConditionValue] = useState(1);
  const [targetField, setTargetField] = useState(1);
  const [xorField, setXorField] = useState(14);
  const [isEnabled, setIsEnabled] = useState(false);
  const [decodeResult, setDecodeResult] = useState(null);
  const [error, setError] = useState(null);
  
  const [manualXorInput, setManualXorInput] = useState('');
  const [manualXorValue, setManualXorValue] = useState('');
  const [manualXorResult, setManualXorResult] = useState(null);
  const [manualXorError, setManualXorError] = useState(null);

  const originalParts = useMemo(() => {
    try {
      const decoded = decodeProto(originalData, false);
      return decoded.parts;
    } catch (err) {
      console.error('Failed to decode original data:', err);
      return [];
    }
  }, [originalData]);

  const availableFields = useMemo(() => {
    const fields = new Set();
    originalParts.forEach(part => {
      if (part.index > 0) {
        fields.add(part.index);
      }
    });
    return Array.from(fields).sort((a, b) => a - b);
  }, [originalParts]);

  const fieldOptions = useMemo(() => {
    return availableFields.map(field => ({
      key: field,
      text: `Field ${field}`,
      value: field
    }));
  }, [availableFields]);

  const performManualXor = () => {
    try {
      setManualXorError(null);
      
      if (!manualXorInput.trim()) {
        setManualXorError('Please enter input data');
        return;
      }
      
      if (!manualXorValue.trim()) {
        setManualXorError('Please enter XOR value');
        return;
      }
      
      const inputBuffer = parseInput(manualXorInput);
      if (!inputBuffer || inputBuffer.length === 0) {
        setManualXorError('Invalid input data format');
        return;
      }
      
      const xorValue = parseInt(manualXorValue);
      if (isNaN(xorValue)) {
        setManualXorError('Invalid XOR value (must be a number)');
        return;
      }
      
      const resultBuffer = xorBuffer(inputBuffer, xorValue);
      
      let decodedResult = null;
      try {
        decodedResult = decodeProto(resultBuffer, false);
      } catch (decodeErr) {
        console.log('Failed to decode XOR result as protobuf:', decodeErr.message);
      }
      
      setManualXorResult({
        inputData: inputBuffer,
        xorValue,
        resultData: resultBuffer,
        decodedResult
      });
      
    } catch (err) {
      setManualXorError(`Manual XOR calculation failed: ${err.message}`);
    }
  };

  const performSmartDecode = () => {
    try {
      setError(null);
      
      const conditionFieldData = getFieldValue(originalParts, conditionField);
      if (!conditionFieldData) {
        setError(`Condition field ${conditionField} not found`);
        return;
      }

      const condition = {
        type: 'varint',
        value: conditionFieldData.value,
        expectedValue: conditionValue
      };

      if (!checkFieldCondition(conditionFieldData, condition)) {
        setError(`Condition not met: Field ${conditionField} value does not match ${conditionValue}`);
        return;
      }

      const targetFieldData = getFieldValue(originalParts, targetField);
      if (!targetFieldData) {
        setError(`Target field ${targetField} not found`);
        return;
      }

      const xorFieldData = getFieldValue(originalParts, xorField);
      if (!xorFieldData) {
        setError(`XOR field ${xorField} not found`);
        return;
      }

      let xorValue;
      if (xorFieldData.type === 0) { // VARINT
        xorValue = parseInt(xorFieldData.value.toString());
      } else {
        setError(`XOR field ${xorField} is not a varint type`);
        return;
      }

      const targetBytes = targetFieldData.value;
      const decryptedBytes = xorBuffer(targetBytes, xorValue);

      try {
        const decodedResult = decodeProto(decryptedBytes, false);
        setDecodeResult({
          originalField: targetFieldData,
          xorField: xorFieldData,
          conditionField: conditionFieldData,
          decryptedBytes,
          decodedResult,
          xorValue
        });
        onDecodeResult && onDecodeResult(decodedResult);
      } catch (decodeErr) {
        setError(`Failed to decode decrypted data: ${decodeErr.message}`);
      }

    } catch (err) {
      setError(`Smart decode failed: ${err.message}`);
    }
  };

  const panes = [
    {
      menuItem: t('smart_conditional_xor'),
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t('smart_enable')}</label>
                <Button 
                  toggle 
                  active={isEnabled}
                  onClick={() => setIsEnabled(!isEnabled)}
                  color={isEnabled ? 'green' : 'grey'}
                >
                  {isEnabled ? t('enabled') : t('disabled')}
                </Button>
              </Form.Field>
            </Form.Group>

            {isEnabled && (
              <>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label>{t('smart_condition_field')}</label>
                    <Dropdown
                      fluid
                      selection
                      options={fieldOptions}
                      value={conditionField}
                      onChange={(e, { value }) => setConditionField(value)}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>{t('smart_condition_value')}</label>
                    <Form.Input
                      type="number"
                      value={conditionValue}
                      onChange={(e) => setConditionValue(parseInt(e.target.value) || 0)}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>{t('smart_target_field')}</label>
                    <Dropdown
                      fluid
                      selection
                      options={fieldOptions}
                      value={targetField}
                      onChange={(e, { value }) => setTargetField(value)}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>{t('smart_xor_field')}</label>
                    <Dropdown
                      fluid
                      selection
                      options={fieldOptions}
                      value={xorField}
                      onChange={(e, { value }) => setXorField(value)}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Button 
                  primary 
                  fluid 
                  onClick={performSmartDecode}
                  disabled={!isEnabled}
                >
                  <Icon name="play" />
                  {t('smart_run')}
                </Form.Button>
              </>
            )}
          </Form>

          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {decodeResult && (
            <Segment style={{ width: '100%' }}>
              <Header as="h5">
                <Icon name="check circle" color="green" />
                {t('smart_result')}
              </Header>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>Condition:</strong> Field {conditionField} = {conditionValue} ✓
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>XOR Operation:</strong> Field {targetField} ⊕ Field {xorField} ({decodeResult.xorValue})
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>Decrypted Data:</strong>
                <div style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '11px',
                  backgroundColor: '#f8f9fa',
                  padding: '8px',
                  borderRadius: '4px',
                  marginTop: '4px',
                  overflowX: 'auto'
                }}>
                  {bufferToPrettyHex(decodeResult.decryptedBytes)}
                </div>
              </div>
              
              <div>
                <strong>Decoded Result:</strong>
                <div style={{ 
                  marginTop: '8px',
                  width: '100%',
                  overflowX: 'auto'
                }}>
                  <ProtobufDisplay value={decodeResult.decodedResult} originalData={decodeResult.decryptedBytes} />
                </div>
              </div>

              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button
                  color="green"
                  onClick={() => {
                    if (typeof onApplyBytes === 'function') {
                      onApplyBytes(decodeResult.decryptedBytes, {
                        label: `Field ${targetField} XOR Field ${xorField} (${decodeResult.xorValue})`,
                      });
                    }
                  }}
                >
                  <Icon name="check" />
                  {t('smart_use_as_input')}
                </Button>
              </div>
            </Segment>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: t('smart_manual_xor'),
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t('smart_input_data')}</label>
                <TextArea
                  placeholder="Enter hex data (e.g., f7 c7 87 f6 da 3b 35 26 33 33 7b 70)
Or Java decimal array (e.g., 247, 199, 135, 246, 218, 59, 53, 38, 51, 51, 123, 112)
Or Java escape string (e.g., \367\307\207\366\332\073\065\046\063\063\173\160)"
                  value={manualXorInput}
                  onChange={(e) => setManualXorInput(e.target.value)}
                  rows={4}
                />
              </Form.Field>
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field>
                <label>{t('smart_xor_value')}</label>
                <Form.Input
                  placeholder="Enter XOR value (e.g., 1807667)"
                  value={manualXorValue}
                  onChange={(e) => setManualXorValue(e.target.value)}
                />
              </Form.Field>
            </Form.Group>

            <Form.Button 
              primary 
              fluid 
              onClick={performManualXor}
            >
              <Icon name="calculator" />
              {t('smart_calculate')}
            </Form.Button>
          </Form>

          {manualXorError && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{manualXorError}</p>
            </Message>
          )}

          {manualXorResult && (
            <Segment style={{ width: '100%' }}>
              <Header as="h5">
                <Icon name="check circle" color="green" />
                Manual XOR Result
              </Header>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>Input Data:</strong>
                <div style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '11px',
                  backgroundColor: '#f8f9fa',
                  padding: '8px',
                  borderRadius: '4px',
                  marginTop: '4px',
                  overflowX: 'auto'
                }}>
                  {bufferToPrettyHex(manualXorResult.inputData)}
                </div>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>XOR Value:</strong> {manualXorResult.xorValue}
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <strong>XOR Result:</strong>
                <div style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '11px',
                  backgroundColor: '#f8f9fa',
                  padding: '8px',
                  borderRadius: '4px',
                  marginTop: '4px',
                  overflowX: 'auto'
                }}>
                  {bufferToPrettyHex(manualXorResult.resultData)}
                </div>
              </div>
              
              {manualXorResult.decodedResult && (
                <div>
                  <strong>Decoded Result (if valid protobuf):</strong>
                  <div style={{ 
                    marginTop: '8px',
                    width: '100%',
                    overflowX: 'auto'
                  }}>
                    <ProtobufDisplay value={manualXorResult.decodedResult} originalData={manualXorResult.resultData} />
                  </div>
                </div>
              )}
            </Segment>
          )}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Segment style={{ width: '100%' }}>
      <Header as="h4">
        <Icon name="magic" />
        <Header.Content>
          {t('smart_decoder')}
          <Header.Subheader>
            {t('smart_subtitle')}
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Tab panes={panes} />
    </Segment>
  );
}

export default SmartDecoder; 